import React from 'react';
import { motion } from 'framer-motion';
import { Users, Award, BookOpen, MapPin, Phone, Mail, Calendar, Target, Heart, Lightbulb, Globe, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutUsPage: React.FC = () => {
  const stats = [
    { number: "50,000+", label: "Students Guided", icon: <Users className="h-8 w-8" /> },
    { number: "500+", label: "Partner Colleges", icon: <BookOpen className="h-8 w-8" /> },
    { number: "20+", label: "Branches in Tamil Nadu", icon: <MapPin className="h-8 w-8" /> },
    { number: "95%", label: "Success Rate", icon: <Trophy className="h-8 w-8" /> },
    { number: "24+", label: "Years Experience", icon: <Calendar className="h-8 w-8" /> },
    { number: "50+", label: "Global Partners", icon: <Globe className="h-8 w-8" /> },
    { number: "15,000+", label: "Abroad Clients", icon: <Award className="h-8 w-8" /> },
    { number: "50+", label: "PhD Experts", icon: <Target className="h-8 w-8" /> }
  ];

  const values = [
    {
      icon: <Target className="h-12 w-12" />,
      title: "Mission",
      description: "To bridge the gap between aspiring students and quality educational institutions, providing comprehensive guidance for a brighter future."
    },
    {
      icon: <Lightbulb className="h-12 w-12" />,
      title: "Vision", 
      description: "To be the leading educational institute in Tamil Nadu, empowering every student to achieve their academic dreams and career goals."
    },
    {
      icon: <Heart className="h-12 w-12" />,
      title: "Values",
      description: "Integrity, Excellence, Student-First Approach, Innovation in Education, and Commitment to Quality Service."
    }
  ];

  const milestones = [
    { year: "2001", event: "SEWA Founded", description: "Started our journey with a vision to help students" },
    { year: "2005", event: "First 1000 Students", description: "Reached our first milestone of successfully guiding 1000 students" },
    { year: "2010", event: "Expanded Services", description: "Added scholarship guidance and international education services" },
    { year: "2015", event: "Digital Transformation", description: "Launched online platform and digital services" },
    { year: "2018", event: "Multi-Branch Expansion", description: "Established 20+ branches across Tamil Nadu for better accessibility" },
    { year: "2020", event: "25,000+ Success Stories", description: "Celebrated helping over 25,000 students achieve their dreams" },
    { year: "2025", event: "50,000+ Students Guided", description: "Continuing to grow and serve the student community" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-slate-900 to-blue-950 text-white overflow-hidden relative">
      {/* Enhanced Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Gradient background with animated waves */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800"
          animate={{ 
            background: [
              'linear-gradient(45deg, #0f172a 0%, #1e3a8a 50%, #1e293b 100%)',
              'linear-gradient(45deg, #1e293b 0%, #3730a3 50%, #0f172a 100%)',
              'linear-gradient(45deg, #0f172a 0%, #1e3a8a 50%, #1e293b 100%)'
            ]
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity, 
            ease: "easeInOut"
          }}
        />

        {/* Large animated orbs with enhanced effects */}
        <motion.div 
          className="absolute -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-3xl"
          animate={{ 
            y: [0, -50, 0],
            x: [0, 30, 0],
            scale: [1, 1.5, 1],
            rotate: [0, 180, 360]
          }}
          transition={{ 
            duration: 25, 
            repeat: Infinity, 
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-gradient-to-tr from-purple-500/15 to-cyan-500/15 rounded-full blur-3xl"
          animate={{ 
            y: [0, 30, 0],
            x: [0, -20, 0],
            scale: [1, 1.3, 1],
            rotate: [360, 180, 0]
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            ease: "easeInOut",
            delay: 2
          }}
        />
        <motion.div 
          className="absolute top-1/3 right-1/3 w-64 h-64 bg-gradient-to-l from-cyan-500/10 to-pink-500/10 rounded-full blur-3xl"
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.4, 1],
            opacity: [0.3, 0.8, 0.3]
          }}
          transition={{ 
            duration: 18, 
            repeat: Infinity, 
            ease: "linear"
          }}
        />

        {/* Animated geometric shapes */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-32 h-32 border-2 border-blue-400/20 transform rotate-45"
          animate={{
            rotate: [45, 405],
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.6, 0.2]
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-1/3 right-1/4 w-24 h-24 border border-purple-400/30 rounded-full"
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.3, 0.7, 0.3],
            rotate: [0, -360]
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />

        {/* Enhanced floating particles */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: `${Math.random() * 8 + 4}px`,
              height: `${Math.random() * 8 + 4}px`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: i % 3 === 0 ? 
                'linear-gradient(45deg, #3b82f6, #8b5cf6)' : 
                i % 3 === 1 ? 
                'linear-gradient(45deg, #8b5cf6, #06b6d4)' :
                'linear-gradient(45deg, #06b6d4, #ec4899)'
            }}
            animate={{
              y: [0, -80, 0],
              x: [0, Math.random() * 40 - 20, 0],
              opacity: [0.2, 0.8, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 8 + Math.random() * 8,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 5,
            }}
          />
        ))}

        {/* Animated light rays */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={`ray-${i}`}
            className="absolute w-1 h-40 bg-gradient-to-t from-transparent via-blue-400/20 to-transparent transform origin-bottom"
            style={{
              left: `${15 + i * 15}%`,
              bottom: '0%',
              transformOrigin: 'bottom center'
            }}
            animate={{
              rotate: [0, 10, -10, 0],
              opacity: [0.1, 0.5, 0.1],
              scaleY: [1, 1.5, 1]
            }}
            transition={{
              duration: 6 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.8
            }}
          />
        ))}

        {/* Floating diamond shapes */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={`diamond-${i}`}
            className="absolute w-4 h-4 bg-gradient-to-br from-cyan-400/30 to-purple-400/30 transform rotate-45"
            style={{
              left: `${Math.random() * 80 + 10}%`,
              top: `${Math.random() * 80 + 10}%`,
            }}
            animate={{
              rotate: [45, 405],
              y: [0, -60, 0],
              opacity: [0.2, 0.8, 0.2],
              scale: [0.8, 1.2, 0.8]
            }}
            transition={{
              duration: 10 + Math.random() * 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 3
            }}
          />
        ))}

        {/* Grid pattern overlay with animation */}
        <motion.div 
          className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-900/5 to-transparent"
          animate={{
            opacity: [0.3, 0.6, 0.3]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <div 
            className="w-full h-full opacity-10 bg-gradient-to-r from-blue-500/10 to-transparent"
            style={{
              backgroundImage: `repeating-linear-gradient(0deg, rgba(59, 130, 246, 0.1) 0px, transparent 1px, transparent 50px, rgba(59, 130, 246, 0.1) 51px), repeating-linear-gradient(90deg, rgba(59, 130, 246, 0.1) 0px, transparent 1px, transparent 50px, rgba(59, 130, 246, 0.1) 51px)`
            }}
          />
        </motion.div>
      </div>

      {/* Content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <motion.section 
          className="pt-20 pb-16 px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="max-w-7xl mx-auto text-center">
            <motion.h1 
              className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 drop-shadow-2xl">
                About 
              </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 drop-shadow-2xl ml-4">
                SEWA
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-xl md:text-2xl text-blue-100 mb-12 max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-purple-300">
                Empowering Dreams, Shaping Futures Since 2001
              </span>
            </motion.p>

            <motion.div 
              className="text-lg text-gray-300 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Your trusted partner in educational excellence, guiding students across Tamil Nadu towards their academic and career aspirations.
            </motion.div>
          </div>
        </motion.section>

        {/* Stats Section */}
        <motion.section 
          className="py-16 px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className="text-center backdrop-blur-lg bg-white/5 border border-blue-400/20 rounded-xl p-6 hover:shadow-2xl hover:shadow-blue-400/20 transition-all duration-300"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.05, y: -10 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <motion.div 
                    className="text-blue-400 mb-4 flex justify-center"
                    whileHover={{ rotate: 360, scale: 1.2 }}
                    transition={{ duration: 0.6 }}
                  >
                    {stat.icon}
                  </motion.div>
                  <motion.div 
                    className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 mb-2"
                    initial={{ scale: 0 }}
                    whileInView={{ scale: 1 }}
                    transition={{ duration: 0.6, delay: index * 0.1 + 0.3 }}
                  >
                    {stat.number}
                  </motion.div>
                  <div className="text-gray-300 font-medium">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Foundation Story */}
        <motion.section 
          className="py-16 px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto">
            <motion.h2 
              className="text-4xl md:text-5xl font-bold text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400">
                Our Foundation Story
              </span>
            </motion.h2>

            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <div className="backdrop-blur-lg bg-white/5 border border-blue-400/20 rounded-2xl p-8 hover:shadow-2xl hover:shadow-blue-400/20 transition-all duration-300">
                  <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 mb-6">
                    The Beginning
                  </h3>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    Founded in <span className="text-blue-400 font-bold">2001</span> in the heart of Tamil Nadu, SEWA began with a simple yet powerful vision: to democratize access to quality education for every aspiring student.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    Starting from our humble office in <span className="text-purple-400 font-bold">Manickampalayam, Tiruchengode</span>, we recognized the challenges students faced in navigating the complex educational landscape.
                  </p>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    Today, with <span className="text-cyan-400 font-bold">20+ branches</span> strategically located across Tamil Nadu, we ensure that quality educational guidance is accessible to students from every corner of the state.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    What started as a local initiative has grown into Tamil Nadu's most trusted educational institute, helping over <span className="text-cyan-400 font-bold">50,000 students</span> achieve their dreams.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="backdrop-blur-lg bg-white/5 border border-purple-400/20 rounded-2xl p-8 hover:shadow-2xl hover:shadow-purple-400/20 transition-all duration-300">
                  <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400 mb-6">
                    Our Impact
                  </h3>
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <MapPin className="h-6 w-6 text-blue-400" />
                      <span className="text-gray-300">Headquarters: Manickampalayam, Tiruchengode, Namakkal</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Globe className="h-6 w-6 text-purple-400" />
                      <span className="text-gray-300">Serving students across Tamil Nadu and South India</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Award className="h-6 w-6 text-cyan-400" />
                      <span className="text-gray-300">Recognized for excellence in educational guidance</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="h-6 w-6 text-pink-400" />
                      <span className="text-gray-300">24/7 support: +91 77086 77132</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Mail className="h-6 w-6 text-blue-400" />
                      <span className="text-gray-300">infonammasewa@gmail.com</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Mission, Vision, Values */}
        <motion.section 
          className="py-16 px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="max-w-7xl mx-auto">
            <motion.h2 
              className="text-4xl md:text-5xl font-bold text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400">
                Our Core Values
              </span>
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <motion.div
                  key={value.title}
                  className="backdrop-blur-lg bg-white/5 border border-blue-400/20 rounded-2xl p-8 text-center hover:shadow-2xl hover:shadow-blue-400/20 transition-all duration-300"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  whileHover={{ scale: 1.05, y: -10 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  viewport={{ once: true }}
                >
                  <motion.div 
                    className="text-blue-400 mb-6 flex justify-center"
                    whileHover={{ rotate: 360, scale: 1.3 }}
                    transition={{ duration: 0.8 }}
                  >
                    {value.icon}
                  </motion.div>
                  <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 mb-4">
                    {value.title}
                  </h3>
                  <p className="text-gray-300 leading-relaxed">
                    {value.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Timeline */}
        <motion.section 
          className="py-16 px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="max-w-6xl mx-auto">
            <motion.h2 
              className="text-4xl md:text-5xl font-bold text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400">
                Our Journey
              </span>
            </motion.h2>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-blue-400 via-purple-400 to-cyan-400 rounded-full opacity-30"></div>

              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  className={`flex items-center mb-12 ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <motion.div 
                      className="backdrop-blur-lg bg-white/5 border border-blue-400/20 rounded-xl p-6 hover:shadow-2xl hover:shadow-blue-400/20 transition-all duration-300"
                      whileHover={{ scale: 1.02, y: -5 }}
                    >
                      <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 mb-2">
                        {milestone.year}
                      </div>
                      <h3 className="text-xl font-bold text-cyan-400 mb-3">
                        {milestone.event}
                      </h3>
                      <p className="text-gray-300">
                        {milestone.description}
                      </p>
                    </motion.div>
                  </div>

                  {/* Timeline dot */}
                  <motion.div 
                    className="w-6 h-6 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full border-4 border-slate-900 z-10"
                    whileHover={{ scale: 1.5 }}
                    transition={{ duration: 0.3 }}
                  />

                  <div className="flex-1"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Call to Action */}
        <motion.section 
          className="py-16 px-4 sm:px-6 lg:px-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2 
              className="text-4xl md:text-5xl font-bold mb-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400">
                Ready to Start Your Journey?
              </span>
            </motion.h2>
            
            <motion.p 
              className="text-xl text-gray-300 mb-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Join thousands of successful students who trusted SEWA for their educational journey.
            </motion.p>

            <motion.div 
              className="flex flex-col sm:flex-row gap-6 justify-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Link to="/premium-services">
                <motion.button 
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-2xl hover:shadow-blue-500/25"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Get Free Guidance
                </motion.button>
              </Link>
              <Link to="/premium-services">
                <motion.button 
                  className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-2xl hover:shadow-purple-500/25"
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Premium Services
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default AboutUsPage;
